/**
 * What the objective will miss (08 §2.5, 03 §2, 10 §2): the items a mission cannot be completed without, and
 * the warnings the loadout screen raises about them. Pure. They inform; nothing here ever blocks DEPLOY.
 *
 * Every line is driven by the mission's FACTS (src/meta/content/archetypeFacts.ts) - its `needs`, whether a
 * mission item rides in a pod, whether there is a drop at all, whether it is knife-only on the way in - so
 * the screen says the same thing as the briefing, and a new mission needs a row of data, not a branch here.
 */

import type { ArchetypeId } from '../../../sim/types';
import { archetypeInfo } from '../archetypes';
import type { LoadoutState, PackItem } from './model';
import { PACK_ITEMS, POD_LETTERS, totalOf } from './model';

/** "Objective requires": any one of `items`, `count` of them, somewhere in the loadout. */
export interface MissionNeed {
  items: PackItem[];
  count: number;
  /** The real number depends on the ground (two piers or three): never fewer than `count`. */
  atLeast?: boolean;
  /** What they are for: "ONE FOR EACH PIER". */
  use?: string;
}

/** The items an objective cannot be completed without (03 §2, 10 §2). */
export function missionNeeds(archetype: ArchetypeId): MissionNeed[] {
  return archetypeInfo(archetype).facts.needs.map((need) => ({
    items: [...need.items], count: need.count,
    ...(need.atLeast === true ? { atLeast: true } : {}), ...(need.use === undefined ? {} : { use: need.use }),
  }));
}

export type WarningKind =
  | 'objective-needs' | 'only-in-pod' | 'bunkers-need-law' | 'no-first-aid'
  /** A mission item rides in a pod: that pod must be dropped and must land (31, 35). */
  | 'quest-item-pod'
  /** No drop: he starts a prisoner, and his kit - carried and packed alike - waits in the pods (32). */
  | 'captured-start' | 'captured-empty-pods'
  /** Knife only on the way in: the suppressed pistol is the one firearm that keeps the silence. */
  | 'quiet-wants-pistol' | 'quiet-pistol-packed';

export interface LoadoutWarning {
  kind: WarningKind;
  /** `critical`: the objective cannot be done as packed. `caution`: it can, but think. `note`: good to know. */
  severity: 'critical' | 'caution' | 'note';
  items: PackItem[];
  count: number;
  atLeast?: boolean;
  /** What the items are for, as the need states it: "ONE FOR EACH PIER". */
  use?: string;
  /** objective-needs: how many are packed anywhere. only-in-pod: how many are on the ranger. */
  have?: number;
  /** quest-item-pod: "RADIO SET", "A", 8. */
  itemName?: string;
  pod?: string;
  weight?: number;
}

const RANK = { critical: 0, caution: 1, note: 2 } as const;

/** Warnings, most serious first. They inform; nothing here ever blocks DEPLOY (08 §2.5). */
export function loadoutWarnings(archetype: ArchetypeId, state: LoadoutState): LoadoutWarning[] {
  const { facts } = archetypeInfo(archetype);
  const out: LoadoutWarning[] = [];
  // A prisoner's carried kit is taken off him and lands in pod A with the rest (sim/insertion): it all counts.
  const total = (items: readonly PackItem[]): number => items.reduce((sum, item) => sum + totalOf(state, item), 0);
  const carried = (items: readonly PackItem[]): number => items.reduce((sum, item) => sum + state.carried[item], 0);

  for (const need of missionNeeds(archetype)) {
    const have = total(need.items);
    const flags = {
      ...(need.atLeast === true ? { atLeast: true } : {}), ...(need.use === undefined ? {} : { use: need.use }),
    };
    if (have < need.count) {
      out.push({ kind: 'objective-needs', severity: 'critical', items: need.items, count: need.count, have, ...flags });
    } else if (carried(need.items) < need.count) {
      // A pod can land in a ravine, a minefield or the enemy's hands (02 §3.2).
      out.push({
        kind: 'only-in-pod', severity: 'caution', items: need.items, count: need.count, have: carried(need.items),
        ...flags,
      });
    }
  }
  if (facts.startCaptured) {
    out.push({ kind: 'captured-start', severity: 'caution', items: [], count: 0 });
    if (total(PACK_ITEMS) === 0) {
      out.push({ kind: 'captured-empty-pods', severity: 'critical', items: [], count: 0 });
    }
  }
  const quest = facts.questItem;
  if (quest !== null && state.pods.length > quest.pod) {
    out.push({
      kind: 'quest-item-pod', severity: 'caution', items: [], count: 1, itemName: quest.name,
      pod: POD_LETTERS[quest.pod] ?? 'A', weight: quest.weight,
    });
  }
  if (facts.bunkers && total(['laws']) === 0) {
    out.push({ kind: 'bunkers-need-law', severity: 'caution', items: ['laws'], count: 1 });
  }
  if (total(['firstAid']) === 0) {
    out.push({ kind: 'no-first-aid', severity: 'caution', items: ['firstAid'], count: 1 });
  }
  // The pistol comes out of the operation's stock: where there is no stock in play there is nothing to say.
  if (facts.quiet === 'strict' && state.gear !== undefined) {
    const packed = state.gear['suppressed-pistol'] > 0;
    out.push({
      kind: packed ? 'quiet-pistol-packed' : 'quiet-wants-pistol', severity: packed ? 'note' : 'caution', items: [],
      count: 1,
    });
  }
  return out.sort((a, b) => RANK[a.severity] - RANK[b.severity]);
}

/** `1 x TIME BOMB` / `AT LEAST 2 x TIME BOMB - ONE FOR EACH PIER`, with names from `GameApi.describe`. */
export function needText(
  need: { items: readonly PackItem[]; count: number; atLeast?: boolean; use?: string },
  name: (item: PackItem) => string,
): string {
  const what = `${need.atLeast === true ? 'AT LEAST ' : ''}${need.count} x ${need.items.map(name).join(' OR ')}`;
  return need.use === undefined ? what : `${what} - ${need.use}`;
}

export function warningText(w: LoadoutWarning, name: (item: PackItem) => string): string {
  const what = needText(w, name);
  switch (w.kind) {
    case 'objective-needs':
      return `OBJECTIVE REQUIRES ${what}: ${(w.have ?? 0) > 0 ? `ONLY ${w.have} PACKED` : 'NOT PACKED'}`;
    case 'only-in-pod':
      return w.count <= 1
        ? `${what} IS ONLY IN A POD: LOSE THE POD AND THE OBJECTIVE GOES WITH IT`
        : `${what}: ONLY ${w.have ?? 0} ON THE RANGER, THE REST IN PODS - LOSE A POD AND THE OBJECTIVE MAY GO WITH IT`;
    case 'bunkers-need-law':
      return `BUNKERS ON THIS GROUND AND NO ${name('laws')} PACKED`;
    case 'no-first-aid':
      return `NO ${name('firstAid')}: A THIRD WOUND CANNOT BE STOPPED`;
    case 'quest-item-pod':
      return `THE ${w.itemName ?? 'MISSION ITEM'} RIDES IN POD ${w.pod ?? 'A'}: DROP POD ${w.pod ?? 'A'} AND LAND IT`
        + ` INTACT, OR THE MISSION IS LOST AT THE DROP. IT WEIGHS ${w.weight ?? 0} ONCE YOU HAVE IT`;
    case 'captured-start':
      return 'NO DROP: YOU START IN A CELL WITH A KNIFE. WHAT YOU PACK ON THE RANGER WAS TAKEN OFF YOU AND WAITS IN'
        + ' POD A, WITH THE PODS, OUTSIDE THE WIRE. RARE GEAR DOES NOT: IT IS LOST';
    case 'captured-empty-pods':
      return 'NOTHING IS PACKED: YOU WILL BREAK OUT WITH A KNIFE AND FIND NOTHING WAITING';
    case 'quiet-wants-pistol':
      return 'KNIFE ONLY ON THE WAY IN. A SUPPRESSED PISTOL BREAKS NO SILENCE: TAKE ONE IF THE STOCK HOLDS IT';
    case 'quiet-pistol-packed':
      return 'SUPPRESSED PISTOL PACKED: IT BREAKS NO SILENCE ON THE WAY IN';
  }
}
